import {
  BaseRepository,
  IBaseModel,
  IListFetchParams,
  MN_JOBS,
  MN_ORDERS,
  MN_RESOURCES,
  MN_TIMESHEET,
  MODULE,
} from '../common';
import type { JobState, MarkType, OrderState, Prisma } from '@prisma/client';
import {
  JobStateEnum,
  MarkTypeEnum,
  OrderStateEnum,
} from '@jobcost-monorepo/database/prisma/enums';
import * as z from 'zod';
import { checkIfAdmin, KeyValue } from '@jobcost-monorepo/utils';
import HookComponentTimesheet from '../ui/CustomHookComponent/HookComponentTimesheet';
import CrudDatePicker from '../ui/CustomComponents/CrudDatePicker';

type Timesheet = Prisma.$TimesheetPayload['scalars'];

export type ITimesheet = Timesheet &
  Omit<IBaseModel, 'createdAt' | 'updatedAt' | 'id'> & {
    job: KeyValue;
    order: KeyValue;
    resource: KeyValue;
  };

export interface IListTimesheetFetchParams extends IListFetchParams {
  isResourceMandatory?: boolean;
  jobId?: number;
  jobState?: JobState;
  orderId?: number;
  orderState?: OrderState;
  resourceId?: number;
  markType?: MarkType;
  startDate?: string;
  endDate?: string;
  excludeRevenue?: boolean;
}

class TimesheetRepository extends BaseRepository<
  ITimesheet,
  IListTimesheetFetchParams
> {
  constructor(moduleName: MODULE) {
    super(moduleName);
    this.keyValueMapFields = {
      idlinkedField: 'id',
      descriptionlinkedField: ['resourceId', 'operationDate'],
    };
    this.gridFieldsOptions = [
      {
        visible: true,
        position: 0,
        linkedField: 'id',
        type: '',
        align: 'left',
        width: '12rem',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 1,
        linkedField: 'resource.name',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 2,
        linkedField: 'order.name',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 3,
        linkedField: 'job.name',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 4,
        linkedField: 'operationDate',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'date',
      },
      {
        visible: true,
        position: 5,
        linkedField: 'workedHours',
        type: '',
        align: 'right',
        width: 'w-full',
        sortable: false,
        formatType: 'hours',
      },
      {
        visible: true,
        position: 6,
        linkedField: 'markType',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'enum',
        translationModuleName: MN_TIMESHEET,
        enumValues: MarkTypeEnum,
      },
    ];

    this.gridFiltersOptions = [
      {
        position: { x: 0, y: 0 },
        linkedField: 'jobId',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_JOBS,
        translationModuleName: MN_JOBS,
        dependsOnFields: ['resourceId', 'startDate', 'endDate'],
        dependsOnFieldsForSearch: ['resourceId', 'startDate', 'endDate'],
        dependsOnForcedFieldsForSearch: { state: JobStateEnum.Running },
      },
      {
        position: { x: 0, y: 1 },
        linkedField: 'orderId',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_ORDERS,
        translationModuleName: MN_ORDERS,
        dependsOnFields: ['resourceId', 'startDate', 'endDate', 'jobId'],
        dependsOnFieldsForSearch: [
          'resourceId',
          'startDate',
          'endDate',
          'jobId',
        ],
        dependsOnForcedFieldsForSearch: {
          jobState: JobStateEnum.Running,
          orderState: OrderStateEnum.Running,
        },
      },
    ];

    this.formFieldsOptions = [
      {
        visible: false,
        readOnly: true,
        position: { x: 0, y: 0 },
        linkedField: 'id',
        type: 'string',
        width: '12rem',
        formatType: 'input',
        moduleName: MN_TIMESHEET,
        translationModuleName: MN_TIMESHEET,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 0 },
        linkedField: 'operationDate',
        formatType: 'custom',
        customComponent: CrudDatePicker,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 0 },
        linkedField: 'resourceId',
        linkedFieldForDefaultValue: 'resource.name',
        type: 'number',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_TIMESHEET,
        dependsOnFields: ['operationDate'],
        checkIfDisabled: () => !checkIfAdmin(),
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 1 },
        linkedField: 'jobId',
        linkedFieldForDefaultValue: 'job.name',
        type: 'number',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_JOBS,
        translationModuleName: MN_TIMESHEET,
        dependsOnFields: ['operationDate', 'resourceId'],
        dependsOnFieldsForSearch: ['resourceId'],
        dependsOnMappedFieldsForSearch: [{ startDate: 'operationDate' }],
        dependsOnForcedFieldsForSearch: {
          state: JobStateEnum.Running,
          orderState: OrderStateEnum.Running,
        },
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 1 },
        linkedField: 'orderId',
        linkedFieldForDefaultValue: 'order.name',
        type: 'string',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_ORDERS,
        translationModuleName: MN_TIMESHEET,
        dependsOnFields: ['jobId', 'resourceId'],
        dependsOnFieldsForSearch: ['jobId', 'resourceId'],
        dependsOnMappedFieldsForSearch: [{ startDate: 'operationDate' }],
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 1, y: 2 },
        linkedField: 'workedHours',
        type: 'number',
        width: 'w-full',
        formatType: 'hours',
        moduleName: MN_TIMESHEET,
        translationModuleName: MN_TIMESHEET,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 2, y: 2 },
        linkedField: 'markType',
        type: 'string',
        width: 'w-full',
        formatType: 'dropdown',
        enumValues: MarkTypeEnum,
        moduleName: MN_TIMESHEET,
        translationModuleName: MN_TIMESHEET,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 3 },
        linkedField: 'note',
        type: 'string',
        width: 'w-full',
        formatType: 'textArea',
        moduleName: MN_TIMESHEET,
        translationModuleName: MN_TIMESHEET,
      },
    ];

    // @ts-ignore
    this.formValidationSchema = z
      .object({
        id: z.number().nullable().optional(),
        resourceId: z.number({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        }),
        jobId: z
          .number({
            invalid_type_error: 'fieldRequired',
            required_error: 'fieldRequired',
          })
          .nullable()
          .optional(),
        orderId: z
          .number({
            invalid_type_error: 'fieldRequired',
            required_error: 'fieldRequired',
          })
          .nullable()
          .optional(),
        operationDate: z.string({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        }),
        workedHours: z
          .number({
            invalid_type_error: 'fieldRequired',
            required_error: 'fieldRequired',
          })
          .nonnegative({ message: 'nonNegativeNumber' })
          .min(0.1, { message: 'min_1_minute' })
          .max(16, { message: 'max_num_16' }),
        markType: z.string({
          invalid_type_error: 'fieldRequired',
          required_error: 'fieldRequired',
        }),
        note: z.string().max(255, { message: 'max_255' }).nullable().optional(),
      })
      .refine(
        (data) => {
          if (data.markType === 'WorkedHours') {
            console.log(
              data.jobId,
              data.orderId,
              !!data.jobId && !!data.orderId,
            );
            return !!data.jobId && !!data.orderId;
          }
          return true;
        },
        {
          message: 'fieldRequired',
          path: ['orderId'], // This will be shown in the error path
        },
      );

    this.hook = HookComponentTimesheet;
  }
}

export default TimesheetRepository;
